"use client";

import { Routes } from "@/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RoomType } from "@/controllers/channel/schema";
import { cn } from "@/lib/utils";
import { useCallContext } from "@/providers/call-provider";
import { useHomeSidebarStore } from "@/stores/sidebar-store";
import { PropsWithClassName } from "@/types";
import {
  DisconnectButton,
  TrackToggle,
  useConnectionState,
  useRoomContext,
} from "@livekit/components-react";
import { ConnectionState, Track } from "livekit-client";
import { PhoneIcon, PhoneOffIcon, Users2Icon, VideoIcon } from "lucide-react";
import { motion } from "motion/react";
import { useRouter, useSearchParams } from "next/navigation";
import React from "react";
import { toast } from "sonner";

interface Props {
  readonly channelId: string;
}

export function ChatRoomOptions(props: PropsWithClassName<Props>) {
  const router = useRouter();
  const call = useCallContext();
  const searchParams = useSearchParams();
  const isInVideoRoom = searchParams?.get("type") === "video";
  const room = useRoomContext();

  const openRoom = React.useCallback(
    (roomType: RoomType) => () => {
      call.toggle.setOn();
      call.channelId.update(props.channelId);
      // Make sure that the sidebar is closed
      useHomeSidebarStore.getState().update(false);
      React.startTransition(() => {
        router.push(
          Routes.INBOX_CHAT_ROOM(props.channelId, `type=${roomType}`),
        );
      });
    },
    [call.toggle, call.channelId, props.channelId, router],
  );

  const startVoiceRoom = React.useCallback(async () => {
    // Check if microphone permission is granted
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(() => {
        call.toggle.setOn();
        call.channelId.update(props.channelId);
      })
      .catch(() => {
        toast.error("Microphone permission is required to join the voice room");
      });
  }, [call.channelId, call.toggle, props.channelId]);

  return (
    <motion.div
      layout
      className={cn("flex items-center space-x-2", props.className)}
    >
      {room.state === ConnectionState.Connected && room.numParticipants > 0 && (
        <div
          className={cn(
            "flex animate-pulse flex-wrap items-center gap-1 rounded-full",
          )}
        >
          {room.numParticipants}
          <Users2Icon className="size-3 text-white" />
        </div>
      )}
      <TooltipProvider>
        {!isInVideoRoom && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div data-lk-theme="default">
                <Button
                  className="lk-button"
                  variant="ghost"
                  size="icon"
                  onClick={openRoom("video")}
                >
                  <VideoIcon className="size-4" />
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent>Enter video room</TooltipContent>
          </Tooltip>
        )}
      </TooltipProvider>
      {call.token && call.toggle.value ? (
        <div data-lk-theme="default">
          {!isInVideoRoom && (
            <TrackToggle
              source={Track.Source.Microphone}
              className={buttonVariants({
                variant: "ghost",
                size: "icon",
              })}
            />
          )}
        </div>
      ) : null}
      <RoomCall startCall={startVoiceRoom} />
    </motion.div>
  );
}

function RoomCall(props: { startCall: () => void }) {
  const call = useCallContext();
  const connectionState = useConnectionState();

  const disconnect = React.useCallback(() => {
    call.channelId.update(undefined);
  }, [call.channelId]);

  if (connectionState === ConnectionState.Connected) {
    return (
      <div data-lk-theme="default">
        <DisconnectButton
          className={buttonVariants({ variant: "ghost", size: "icon" })}
          onClick={disconnect}
        >
          <PhoneOffIcon className="size-4" />
        </DisconnectButton>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" onClick={props.startCall}>
          <PhoneIcon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Enter voice room</TooltipContent>
    </Tooltip>
  );
}
